/**
 * Conversation list — `GET messaging/conversations/`, scoped to conversations
 * the caller participates in (role-independent — even a SUPERAGENT only sees
 * their own, per `buildScope`). Rows carry only the LAST message, not the
 * full thread — click through to the detail page for the full history.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { listConversations } from '../../api/endpoints/messaging';
import { fullName } from '../../types/user';

export function ConversationListPage(): React.JSX.Element {
  const { user } = useAuth();
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['conversations', { page }],
    queryFn: () => listConversations({ page }),
  });

  const conversations = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  function otherParticipants(row: (typeof conversations)[number]): string {
    const others = row.participants_details.filter((participant) => participant.id !== user?.id);
    if (others.length === 0) return 'Just you';
    return others.map((participant) => fullName(participant)).join(', ');
  }

  return (
    <section>
      <h2>Messages</h2>

      <p>
        <Link to="new">New conversation</Link>
      </p>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}

      {query.isSuccess && conversations.length === 0 && <p>No conversations yet.</p>}

      {query.isSuccess && conversations.length > 0 && (
        <ul>
          {conversations.map((conversation) => (
            <li key={conversation.id}>
              <Link to={`${conversation.id}`}>{otherParticipants(conversation)}</Link>
              {' — '}
              {conversation.last_message === null ? (
                <em>No messages yet</em>
              ) : (
                <span>
                  {conversation.last_message.text === '' ? '(image)' : conversation.last_message.text}
                </span>
              )}
              {' · '}
              <small>{conversation.updated_at}</small>
            </li>
          ))}
        </ul>
      )}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>
    </section>
  );
}

export default ConversationListPage;
